/**
 * Global iOS drop-shadow stabilizer for floating images.
 *
 * iOS Safari intermittently fails to paint an image's `filter: drop-shadow()`
 * on first reveal (lazy decode), and re-renders it on the main thread during
 * transforms — so float shadows "load in" late, then flicker. This runs once
 * per page on touch pointers, finds every <img> that actually has a drop-shadow
 * filter, pins it to its own GPU layer, and forces a one-time repaint after the
 * image decodes.
 *
 * It is class-agnostic (matches on the computed `filter`), so it covers ALL
 * floating-image blocks in ANY section with no per-section code: the universal
 * `floating-image` @theme block (`.fl-block__img`), the PLP `hero-banner`
 * (`.hhb-float__img`), and any future section that gives a float a drop-shadow.
 *
 * Skipped: <hero-product-showcase> — the homepage carousel hydrates/relocates
 * its floats on its own component lifecycle and already self-manages this.
 *
 * Gated to a non-hover (touch) pointer so it is completely inert on
 * hover/desktop, where the shadows paint correctly and a repaint could flash.
 */
(() => {
  // Match the existing per-section gates: skip anything with a real hover pointer.
  if (window.matchMedia('(hover: hover)').matches) return;

  const handled = new WeakSet();

  const stabilize = (img) => {
    if (handled.has(img)) return;
    const filter = getComputedStyle(img).filter;
    if (!filter || filter.indexOf('drop-shadow') === -1) return;
    // The homepage carousel self-manages its floats on its own lifecycle.
    if (img.closest('hero-product-showcase')) return;
    handled.add(img);

    // Pin to a dedicated GPU layer so the shadow rasterizes once and composites.
    // will-change / backface-visibility do NOT override any existing transform.
    img.style.willChange = 'transform';
    img.style.webkitBackfaceVisibility = 'hidden';
    img.style.backfaceVisibility = 'hidden';

    // Force the first paint of the shadow once the image has decoded — this is
    // the part CSS cannot do.
    const repaint = () => {
      img.style.filter = 'none';
      requestAnimationFrame(() => img.style.removeProperty('filter'));
    };
    if (img.complete && img.naturalWidth) repaint();
    else img.addEventListener('load', repaint, { once: true });
  };

  const scan = () => {
    const imgs = document.querySelectorAll('img');
    for (let i = 0; i < imgs.length; i++) stabilize(imgs[i]);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
  // Re-scan after full load to catch images whose shadow styles applied late
  // (lazy decode, deferred stylesheet). The WeakSet keeps it idempotent.
  window.addEventListener('load', scan);
})();
